import { action, makeObservable, observable, runInAction } from "mobx";
import api from "../../api";
import { IAddProduct } from "../../api/product/ProductsApi";
import { shortMonthNames } from "../../components/util/DateTimeUtil";
import { OrderDetail } from "../document/Document";
import { Product } from "./Product";

class ProductStore {
  product?: Product = undefined;

  constructor() {
    makeObservable(this, {
      product: observable,
      getProduct: action.bound,
      setProductProperty: action.bound,
      discontinueProduct: action.bound,
      uploadProductPicture: action.bound,
      addNewProduct: action.bound,
      addToRequisition: action.bound,
      addInstantRebate: action.bound
    });
    console.warn(
      "Deprecation Warning: The legacy 'ProductStore' is in use. Please migrate to RTK Query as outlined in CONTRIBUTING.md."
    );
  }

  async getProduct(applicationId: string, randmarSKU: string) {
    try {
      if (this.product?.randmarSKU !== randmarSKU) {
        this.product = undefined;
      }

      const productWithSpecification = await api.products.retrieveProduct(applicationId, randmarSKU, true);
      const mappedProductWithSpecification = Product.convertFrom(productWithSpecification);
      runInAction(() => { this.product = mappedProductWithSpecification; });

    } catch (e) {
      console.error(e);
    }
  }

  getMonthlySalesData(orders: OrderDetail[], monthsBack: number) {
    const numberToDate = (dateInt: number) => {
      const dateStr = dateInt.toString();
      const year = parseInt(dateStr.slice(0, 4), 10);
      const month = parseInt(dateStr.slice(4, 6), 10) - 1;
      return new Date(year, month);
    };

    const currentDate = new Date();
    const earliestOrderDate = orders.reduce((earliest, item) => {
      if (item.orderDate) {
        const orderDate = numberToDate(item.orderDate);
        return orderDate < earliest ? orderDate : earliest;
      }
      return earliest;
    }, new Date());

    if (monthsBack === Infinity) {
      const totalMonths = (currentDate.getFullYear() - earliestOrderDate.getFullYear()) * 12
        + currentDate.getMonth() - earliestOrderDate.getMonth() + 1;
      monthsBack = totalMonths;
    }

    const lastMonths: { [key: string]: number } = {};

    const getMonthYearKey = (date: Date) => {
      const month = shortMonthNames[date.getMonth()];
      const year = date.getFullYear().toString().slice(-2);
      return `${month} '${year}`;
    };

    for (let i = 0; i < monthsBack; i++) {
      const date = new Date(currentDate);
      date.setMonth(currentDate.getMonth() - i);
      lastMonths[getMonthYearKey(date)] = 0;
    }

    orders.forEach(item => {
      if (item.orderDate) {
        const orderDate = numberToDate(item.orderDate);
        const key = getMonthYearKey(orderDate);
        if (Object.prototype.hasOwnProperty.call(lastMonths, key)) {
          lastMonths[key] += item.quantity ?? 0;
        }
      }
    });

    const currentMonthYearKey = getMonthYearKey(currentDate);
    const daysInCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    const daysElapsed = currentDate.getDate();

    if (lastMonths[currentMonthYearKey] > 0) {
      lastMonths[currentMonthYearKey] = Math.round(
        (lastMonths[currentMonthYearKey] / daysElapsed) * daysInCurrentMonth
      );
    }

    return Object.keys(lastMonths)
      .sort((a, b) => new Date(`1 ${a}`).getTime() - new Date(`1 ${b}`).getTime())
      .map(month => ({
        "Month": monthsBack <= 12 ? month.slice(0, 3) : month,
        "Units Sold": lastMonths[month]
      }));
  }

  async setProductProperty(
    applicationId: string,
    randmarSKU: string,
    propName: string,
    newValue: string
  ): Promise<boolean> {
    try {
      return await api.products.setProductProperty(applicationId, randmarSKU, propName, newValue);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async setProductBodyProperty(applicationId: string, randmarSKU: string, propName: string, newValue: string): Promise<boolean> {
    try {
      return await api.products.setProductBodyProperty(applicationId, randmarSKU, propName, newValue);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async discontinueProduct(applicationId: string, randmarSKU: string): Promise<boolean> {
    try {
      return await api.products.discontinueProduct(applicationId, randmarSKU);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async uploadProductPicture(applicationId: string, randmarSKU: string, selectedFile: File): Promise<boolean> {
    try {
      return await api.products.uploadProductPicture(applicationId, randmarSKU, selectedFile);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async uploadProduct3DModel(applicationId: string, randmarSKU: string, selectedFile: File): Promise<boolean> {
    try {
      return await api.products.addProduct3DModel(applicationId, randmarSKU, selectedFile);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async addNewProduct(applicationId: string, upc: string, product: IAddProduct): Promise<Product | undefined> {
    try {
      const randmarProduct = await api.products.addNewProduct(applicationId, upc, product);

      if (randmarProduct === undefined) return undefined;

      return Product.convertFrom(randmarProduct);

    } catch (e) {
      return undefined;
    }
  }

  async addToRequisition(
    applicationId: string,
    randmarSKU: string,
    location: string,
    quantity: string
  ): Promise<boolean> {
    try {
      return await api.products.addToRequisition(applicationId, randmarSKU, location, quantity);
    } catch (e) {
      return false;
    }
  }

  async addInstantRebate(
    applicationId: string,
    randmarSKU: string,
    rebate: number,
    startDate: string,
    endDate: string,
    promotionId: string
  ): Promise<boolean> {
    try {
      return await api.products.addInstantRebate(applicationId, randmarSKU, rebate, startDate, endDate, promotionId);
    } catch (e) {
      return false;
    }
  }
}

export default ProductStore;
